import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Button } from "../../../ui/button";

/**
 * Generic confirmation dialog. Pass `open`/`onOpenChange` to control externally.
 * Optional `withReason` adds a textarea whose value is passed to onConfirm.
 *
 * Props:
 *  - open, onOpenChange
 *  - title, description
 *  - confirmLabel (default "Confirm"), cancelLabel (default "Cancel")
 *  - destructive: bool — switches confirm button to destructive variant
 *  - withReason: bool — render an optional reason textarea
 *  - reasonLabel: string
 *  - reasonRequired: bool — disable confirm until the reason is non-blank
 *  - onConfirm: async (reason?: string) => void
 */
const ConfirmDialog = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  withReason = false,
  reasonLabel = "Reason (optional)",
  reasonRequired = false,
  onConfirm,
}) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm?.(withReason ? reason.trim() : undefined);
      onOpenChange?.(false);
      setReason("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {withReason && (
          <div className="grid gap-2">
            <label className="text-sm font-medium" htmlFor="confirm-dialog-reason">
              {reasonLabel}
            </label>
            <textarea
              id="confirm-dialog-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={busy}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "destructive" : "default"}
            onClick={handleConfirm}
            disabled={busy || (withReason && reasonRequired && !reason.trim())}
          >
            {busy ? "Working…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDialog;
